import React, { useState, useEffect, useRef } from 'react'
import './App.css'

import { makeStyles } from '@material-ui/core'

import Scale from '@tonaljs/scale'
import Chord from '@tonaljs/chord'
import Note from '@tonaljs/note'
import ScaleType from '@tonaljs/scale-type'

import Guitar from './react-guitar'
import ScaleSettings from './ScaleSettings'

import { simplifyNote } from './util/utils'
import ChordCategoryList from './ChordCategoryList'

import { red, orange, green, lightBlue, teal, cyan } from '@material-ui/core/colors'

import { Tuning } from './tunings'
import DegreesSettings from './DegreesSettings'

import ToneSound from './ToneSound'

import { notes } from './util/TonalUtils'
import { notesToChord, notesToScale } from './util/ToneUtils'
import MyAppBar from './MyAppBar'

const Colors = { red, orange, green, lightBlue, teal, cyan }

const colorPalette = ['red', 'orange', 'green', 'lightBlue', 'teal', 'cyan']

const generateColors = (colorPalette: string[]) => colorPalette.map(color => ({
  // @ts-ignore
  color: Colors[color][400],
  // @ts-ignore
  borderColor: Colors[color][700],
}))

const colors = generateColors(colorPalette)

// const colors = {
//   red: {
//     color: '#e57373',
//     borderColor: '#af4448'
//   },
//   orange: {
//     color: '#ffb74d',
//     borderColor: '#c88719'
//   },
//   green: {
//     color: '#81c784',
//     borderColor: '#519657'
//   },
// }

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
}))


type ScaleTypeType = {
  name: string,
  aliases: string[]
}

const scaleModeCategories = ['pentatonic', 'minor', 'major']

const getScaleModeCategory = (scaleType: ScaleTypeType) => {

  let fullName = scaleType.aliases.join(' ')
  fullName += scaleType.name

  for (let i = 0; i < scaleModeCategories.length; i++) {
    if (fullName.includes(scaleModeCategories[i]))
      return scaleModeCategories[i]
  }

  return 'other'
}

const scalesMode = ScaleType.all().map((scaleType: any) => ({
  value: scaleType.name,
  name: scaleType.aliases.length > 0 ? scaleType.aliases[0] + ' (' + scaleType.name + ')' : scaleType.name,
  category: getScaleModeCategory(scaleType)
}))

// Sort scalesMode by category
scaleModeCategories.forEach(scaleModeCategory => scalesMode.sort((s1: any, s2: any) => s1.category === s2.category ? 0 : s1.category === scaleModeCategory ? -1 : 1))


type MyScaleType = {
  name: string,
  notes: string[],
  tonic: string | undefined,
  type: string | undefined
}

const getStrings = (fretsObject: any, tuning: Tuning, scale: any, chord?: ChordType) => {// TODO : Move into utils
  // scale = Scale.get(scaleName)
  
  // const chord = chordName && Chord.get(chordName)
  
  const tuningNotes = tuning.notes.split(' ').map(simplifyNote)

  // console.log({ scale, chord, notes, tuningNotes })

  const strings: any[] = []

  for (let i = 0; i < tuningNotes.length; i++) {
    const frets: any[] = []

    for (let j = 0; j <= fretsObject.amount; j++) {// TODO : handle from !== 0
      const currentNote = notes[(notes.findIndex(n => n === tuningNotes[i]) + j) % notes.length]
      
      // console.log({ scale: scale.notes, currentNote: Note.enharmonic(currentNote), currentNoteEnharmonic: Note.enharmonic(Note.enharmonic(currentNote)), isInScale: scale.notes.includes(currentNote) || scale.notes.includes(Note.enharmonic(currentNote)) })

      if (scale.notes.map((n: any) => Note.enharmonic(n)).includes(currentNote) || scale.notes.map((n: any) => Note.enharmonic(n)).includes(Note.enharmonic(currentNote))) {

        const chordIndex = chord ? chord.notes.map(n => Note.enharmonic(n)).findIndex(n => n === currentNote || n === Note.enharmonic(currentNote)) : Note.enharmonic(scale.notes[0]) === currentNote || Note.enharmonic(scale.notes[0]) === Note.enharmonic(currentNote) ? 0 : -1

        frets.push({
          nbr: j,
          ...colors[chordIndex]
        })
      }
    }

    strings.push(frets)
  }

  return strings.reverse()
}

type ChordType = {
  symbol: string,
  notes: string[]
}

const romanDegrees = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII']

// const getScaleModeForDegreeFromNotes = (scale: MyScaleType, degree: number) => {
//   const modeNames = Scale.modeNames(scale.name)

//   console.log({ modeNames })

//   return modeNames.find(modeName => modeName[0] === scale.notes[degree])
// }

const getAllDegreesScales = (scale: any) => {
  const modeNames = Scale.modeNames(scale.name)
  
  return scale.notes.map((n: any) => {
    const modeName = modeNames.find(modeName => modeName[0] === n)
    
    if (!modeName) return

    return Scale.get(modeName[0] + ' ' + modeName[1])
  })
}

const getChords = (root: string, scaleName: string) => {
  
  const chords = Scale.scaleChords(scaleName).map((chordType) => Chord.get(root + chordType)).filter(chord => !chord.empty)

  console.log({ root, scaleName, chords })

  return chords
}

const initialScale = Scale.get('C major')

const initialAllDegreesScales = getAllDegreesScales(initialScale)

function usePrevious(value: any) {
  const ref = useRef()
  useEffect(() => {
    ref.current = value
  })
  return ref.current
}

function ScaleVisualizer(props: {
  settings: any,
  setTitle: (title: string) => void,
  fretsObject: any,
  constants: any,
  onSettingsChange: (settings: any) => void
}) {
  const {
    settings,
    setTitle,
    fretsObject,
    constants,
    onSettingsChange
  } = props

  const classes = useStyles()
  
  const [root, setRoot] = useState('C')
  const [scaleMode, setScaleMode] = useState(scalesMode.find(scaleMode => scaleMode.name === 'ionian (major)')!)
  
  const [scale, setScale] = useState<any>(initialScale)
  const prevScale = usePrevious(scale)

  const [strings, setStrings] = useState(getStrings(fretsObject, settings.tuning, initialScale))

  const [chords, setChords] = useState<ChordType[] | undefined>(undefined)
  const [chord, setChord] = useState<ChordType | undefined>(undefined)

  const [degree, setDegree] = useState(0)

  const [allDegreesScales, setAllDegreesScales] = useState<any[]>(initialAllDegreesScales)

  // const [notesToPlay, setNotesToPlay] = useState<any[] | undefined>(undefined)


  console.log('Component log')
  console.log({ root, strings, chords, scalesMode, scaleMode, allDegreesScales, scale })
  

  // Update fretboard when scale settings change (update chords too)
  useEffect(() => {// TODO renders twice at initialisation
    console.log('useEffect root,scaleName', ({ root, strings, chords, scalesMode, scaleMode, allDegreesScales, scale }))

    // if (prevScale && scale && prevScale.name !== scale.name) {
    //   console.log('scale update', ({ prevScale, scale }))
    //   setRoot(scale.tonic)
    //   setScaleMode(scale.type)
    //   setChord(undefined)
    //   setStrings(getStrings(fretsObject, settings.tuning, scale))
    //   setDegree(0)
    //   setAllDegreesScales(getAllDegreesScales(scale))
    //   setTitle(root + ' ' + scaleMode.value + (chord ? ' | ' + chord.symbol : ''))
    //   return
    // }

    const newScale = Scale.get(root + ' ' + scaleMode.value)

    if (scale === newScale) return

    setScale(newScale)
    setChord(undefined)
    setStrings(getStrings(fretsObject, settings.tuning, newScale))
    // setChords(getChords(root, scaleMode.value))
    setDegree(0)
    setChords(getChords(root, scaleMode.value))
    setAllDegreesScales(getAllDegreesScales(newScale))

    setTitle(root + ' ' + scaleMode.value + (chord ? ' | ' + chord.symbol : ''))
  }, [root, scaleMode])

  // Update fretboard
  useEffect(() => {
    setStrings(getStrings(fretsObject, settings.tuning, scale, chord))
    setTitle(root + ' ' + scaleMode.value + (chord ? ' | ' + chord.symbol : ''))
  }, [chord, settings.tuning])

  // When degree change -> update chords
  useEffect(() => {
    setChord(undefined)
    setChords(allDegreesScales[degree] && getChords(allDegreesScales[degree].notes[0], allDegreesScales[degree].name))
    setStrings(getStrings(fretsObject, settings.tuning, allDegreesScales[degree]))
  }, [degree])

  // useEffect(() => {
  //   console.log('useEffect scale', ({ root, strings, chords, scalesMode, scaleMode, allDegreesScales, scale }))

  // }, [scale])

  return (
    <div className={classes.root}>

      <MyAppBar
        constants={constants}
        onSettingsChange={onSettingsChange}
        settings={settings}
      />
      
      <Guitar
        strings={strings}
        // onChange={strings => setStrings(strings)}
        tuningObject={settings.tuning}
        frets={fretsObject}
        isEnharmonic
        scaleNotes={scale.notes}
      />

      <ScaleSettings
        roots={notes}
        scalesMode={scalesMode}
        root={root}
        scaleMode={scaleMode}
        onRootChange={setRoot}
        onScaleModeChange={setScaleMode}
        // @ts-ignore
        onPlayClick={(e: any) => {console.log({ onPlayClick: scale, e }); ToneSound.playNotes(notesToScale(scale.notes), settings.sound)}}
      />

      <DegreesSettings
        degrees={romanDegrees.slice(0, scale.notes.length).map((d: string, i: number) => ({ name: d, empty: !allDegreesScales[i] }))}
        degree={degree}
        scaleNotes={scale.notes}
        onDegreeChange={setDegree}
        onPlayClick={(i) => allDegreesScales[i] && ToneSound.playNotes(notesToScale(allDegreesScales[i].notes), settings.sound)}
        scales={allDegreesScales}
        onScaleChange={scale => { setRoot(scale.tonic); setScaleMode(scalesMode.find((scaleMode: any) => scaleMode.value === scale.type)!) }}// scale.type might not be in scalesMode
      />

      {chords && (
        <ChordCategoryList
          chords={chords}
          chord={chord}
          onChordChange={setChord}
          onPlayClick={(c) => ToneSound.playNotes(notesToChord(c.notes), settings.sound)}
        />
      )}
      
      {/* <Sound
        notes={notesToPlay}
      /> */}

    </div>
  )
}

export default ScaleVisualizer

// TODO : switch from # or b

// DONE : is actual scale is b then show note on scale as b

// TODO : show every possible scale formed out of those intervals (make learning more motivating, you see what you will be able to play with this form)

// TODO : snippet basic components for list (array, selected, onChange)

// TODO : ajouter un code couleur
// TODO : Fixer les couleurs aux intervalles (définir 1 famille d'intervalle correspondant à une couleur (5te et 5te diminué en orange))

// DONE : get scale for each degree of a source scale, then find the chords that matches

// TODO : EN FAIRE UN OUTIL D'AIDE A LA COMPOSITION


// DONE : Play chords

// DONE : Add octave on frets

// FIXME : C Neopolitan Major II wrong note

// NOTE : Kumoi scale nice color, Kumoijoshi too

// TODO : Fix flashing frets (may be caused by 2 setStates)

// TODO : Add piano visualizer

// TODO : use https://github.com/tonaljs/tonal/tree/master/packages/key to find scales for each degree
  // also give triads-chords for each degree
// TODO : switch between basic mode and advance mode ?